/**
 * Helper functions for book operations
 */

/**
 * Format reading progress as percentage
 */
export function getBookProgress(book) {
  if (!book.totalPages) return 0;
  return Math.round(((book.currentPage ?? 0) / book.totalPages) * 100);
}

/**
 * Get book status badge text
 */
export function getStatusLabel(status) {
  const labels = {
    tbr: 'To Be Read',
    reading: 'Reading',
    finished: 'Finished',
    favorite: 'Favorite',
  };
  return labels[status];
}

/**
 * Get reading status icon
 */
export function getStatusIcon(status) {
  const icons = {
    tbr: '📚',
    reading: '👁️',
    finished: '✓',
    favorite: '⭐',
  };
  return icons[status];
}

/**
 * Format date to readable string
 */
export function formatDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate reading time estimate
 */
export function getReadingTimeEstimate(
  totalPages,
  wordsPerMinute = 200,
  wordsPerPage = 250
) {
  const totalWords = totalPages * wordsPerPage;
  const minutes = Math.ceil(totalWords / wordsPerMinute);
  const hours = Math.ceil(minutes / 60);
  const days = Math.ceil(hours / 4); // Assuming 4 hours of reading per day

  if (days < 1) return `${hours} hour`;
  if (days < 30) return `${days} days`;
  const weeks = Math.ceil(days / 7);
  return `${weeks} weeks`;
}

/**
 * Check if book is currently being read
 */
export function isCurrentlyReading(book) {
  return book.status === 'reading';
}

/**
 * Check if book is finished
 */
export function isFinished(book) {
  return book.status === 'finished';
}

/**
 * Check if book is in TBR
 */
export function inTBR(book) {
  return book.status === 'tbr';
}

/**
 * Get cover aspect ratio
 */
export function getCoverAspectRatio() {
  // Typical book cover aspect ratio
  return '2 / 3';
}

/**
 * Generate cover placeholder
 */
export function getCoverPlaceholder(book) {
  // Return a placeholder with initials
  const initials = book.title
    .split(' ')
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  return initials;
}

/**
 * Sort books by date added
 */
export function sortByDateAdded(books) {
  return [...books].sort((a, b) => b.dateAdded - a.dateAdded);
}

/**
 * Sort books by title
 */
export function sortByTitle(books) {
  return [...books].sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Sort books by author
 */
export function sortByAuthor(books) {
  return [...books].sort((a, b) => a.author.localeCompare(b.author));
}

/**
 * Sort books by rating
 */
export function sortByRating(books) {
  return [...books].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
}

/**
 * Sort books by reading progress
 */
export function sortByProgress(books) {
  return [...books].sort(
    (a, b) => (b.percentageRead ?? 0) - (a.percentageRead ?? 0)
  );
}

/**
 * Filter books by genre
 */
export function filterByGenre(books, genres) {
  if (genres.length === 0) return books;
  return books.filter(b =>
    b.genres.some(g => genres.includes(g))
  );
}

/**
 * Filter books by status
 */
export function filterByStatus(books, statuses) {
  if (statuses.length === 0) return books;
  return books.filter(b => statuses.includes(b.status));
}

/**
 * Filter books by rating
 */
export function filterByMinRating(books, minRating) {
  return books.filter(b => (b.rating ?? 0) >= minRating);
}

/**
 * Filter favorites
 */
export function filterFavorites(books) {
  return books.filter(b => b.isFavorite);
}

/**
 * Search books by title or author
 */
export function searchBooks(books, query) {
  if (!query.trim()) return books;
  const lowerQuery = query.toLowerCase();
  return books.filter(
    b =>
      b.title.toLowerCase().includes(lowerQuery) ||
      b.author.toLowerCase().includes(lowerQuery)
  );
}
